/**
 * Floating chat bubble component
 */

import type { WidgetConfig, WidgetPosition } from '../core/config';
import { BADGE_COUNT_LIMIT, BUBBLE_PULSE_DURATION_MS } from '../core/constants';

export interface BubbleCallbacks {
  onToggle: (isOpen: boolean) => void;
}

// Store click handlers for cleanup - WeakMap allows GC when bubble is removed
const bubbleHandlers = new WeakMap<HTMLElement, () => void>();

// Store pulse timers for cleanup - allows cancellation when bubble is destroyed
const pulseTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();

/**
 * Create the floating chat bubble element
 */
export function createBubble(
  config: WidgetConfig,
  callbacks: BubbleCallbacks
): HTMLElement {
  const position = config.position ?? 'bottom-right';
  const theme = config.theme ?? {};

  const bubble = document.createElement('button');
  bubble.className = `pms-bubble pms-bubble-${position}`;
  bubble.setAttribute('aria-label', 'Open chat');
  bubble.setAttribute('aria-expanded', 'false');
  bubble.setAttribute('type', 'button');

  // Set custom properties — only bubbleSize; accent color is inherited
  // from the container via CSS cascade (palette / theme / default).
  if (theme.bubbleSize) {
    bubble.style.setProperty('--pms-bubble-size', theme.bubbleSize);
  }

  // Bubble margin offsets (configurable via settings)
  if (config.marginBottom !== undefined) {
    bubble.style.setProperty('--pms-bubble-margin-bottom', `${config.marginBottom}px`);
  }
  if (config.marginSide !== undefined) {
    bubble.style.setProperty('--pms-bubble-margin-side', `${config.marginSide}px`);
  }

  // Chat icon
  bubble.innerHTML = `
    <span class="pms-bubble-icon pms-bubble-icon-chat">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    </span>
    <span class="pms-bubble-icon pms-bubble-icon-close">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </span>
    <span class="pms-bubble-badge"></span>
  `;

  // Store handler reference for cleanup
  // Read state from DOM class (not a closure variable) so external setBubbleOpen() stays in sync
  const clickHandler = () => {
    const wasOpen = bubble.classList.contains('pms-bubble-open');
    const nowOpen = !wasOpen;
    bubble.classList.toggle('pms-bubble-open', nowOpen);
    bubble.setAttribute('aria-label', nowOpen ? 'Close chat' : 'Open chat');
    bubble.setAttribute('aria-expanded', String(nowOpen));
    callbacks.onToggle(nowOpen);
  };

  bubble.addEventListener('click', clickHandler);
  bubbleHandlers.set(bubble, clickHandler);

  return bubble;
}

/**
 * Clean up bubble event listeners and timers to prevent memory leaks
 */
export function destroyBubble(bubble: HTMLElement): void {
  // Clear click handler
  const handler = bubbleHandlers.get(bubble);
  if (handler) {
    bubble.removeEventListener('click', handler);
    bubbleHandlers.delete(bubble);
  }

  // Clear any active pulse timer
  const pulseTimer = pulseTimers.get(bubble);
  if (pulseTimer) {
    clearTimeout(pulseTimer);
    pulseTimers.delete(bubble);
  }
}

/**
 * Get position styles for bubble placement
 */
export function getBubblePositionStyles(position: WidgetPosition): string {
  const baseStyles = `
    position: fixed;
    z-index: 999998;
  `;

  switch (position) {
    case 'bottom-left':
      return `${baseStyles} bottom: 20px; left: 20px;`;
    case 'bottom-right':
    default:
      return `${baseStyles} bottom: 20px; right: 20px;`;
  }
}

/**
 * Show unread badge on bubble
 */
export function showBubbleBadge(bubble: HTMLElement, count?: number): void {
  const badge = bubble.querySelector('.pms-bubble-badge');
  if (badge instanceof HTMLElement) {
    badge.classList.add('pms-bubble-badge-visible');
    if (count !== undefined && count > 0) {
      badge.textContent = count > BADGE_COUNT_LIMIT ? `${BADGE_COUNT_LIMIT}+` : String(count);
    } else {
      badge.textContent = '';
    }
  }
}

/**
 * Hide unread badge on bubble
 */
export function hideBubbleBadge(bubble: HTMLElement): void {
  const badge = bubble.querySelector('.pms-bubble-badge');
  if (badge instanceof HTMLElement) {
    badge.classList.remove('pms-bubble-badge-visible');
    badge.textContent = '';
  }
}

/**
 * Set bubble open state
 */
export function setBubbleOpen(bubble: HTMLElement, isOpen: boolean): void {
  bubble.classList.toggle('pms-bubble-open', isOpen);
  bubble.setAttribute('aria-label', isOpen ? 'Close chat' : 'Open chat');
  bubble.setAttribute('aria-expanded', String(isOpen));
}

/**
 * Pulse animation for attention.
 * Timer is tracked for cleanup if bubble is destroyed mid-animation.
 */
export function pulseBubble(bubble: HTMLElement): void {
  // Clear any existing pulse timer
  const existingTimer = pulseTimers.get(bubble);
  if (existingTimer) {
    clearTimeout(existingTimer);
  }

  bubble.classList.add('pms-bubble-pulse');

  const timerId = setTimeout(() => {
    bubble.classList.remove('pms-bubble-pulse');
    pulseTimers.delete(bubble);
  }, BUBBLE_PULSE_DURATION_MS);

  pulseTimers.set(bubble, timerId);
}
